import i18next from 'i18next';
import React, { useEffect, useState } from 'react';
import { DTFItem } from './packing';
import { QualityInfo } from './quality';
import { rangeText } from './QualityModal';
import imgMagic from './mascotte/magic_wand.webp';
import imgPen from './mascotte/pen_vector.webp';
import imgPoint from './mascotte/point_up.webp';
import imgPresent from './mascotte/present.webp';
import imgScissors from './mascotte/scissors_cut.webp';
import imgTablet from './mascotte/tablet_design.webp';
import imgThinking from './mascotte/thinking.webp';
import imgThumbs from './mascotte/thumbs_up.webp';

export type MarmottePose =
	| 'present'
	| 'vector'
	| 'thumbs'
	| 'thinking'
	| 'magic'
	| 'scissors'
	| 'point'
	| 'design';

export type MarmotteTone = 'ok' | 'warn' | 'bad' | 'info';

export const MARMOTTE_POSES: Record<MarmottePose, string> = {
	present: imgPresent,
	vector: imgPen,
	thumbs: imgThumbs,
	thinking: imgThinking,
	magic: imgMagic,
	scissors: imgScissors,
	point: imgPoint,
	design: imgTablet,
};

interface IProps {
	pose: MarmottePose;
	tone?: MarmotteTone;
	/** ligne en gras au-dessus du texte */
	title?: string;
	size?: number;
	className?: string;
	/** marmotte vedette : halo lumineux, étincelles, entrée rebond (accueil) */
	hero?: boolean;
	/** texte de la bulle tapé façon machine à écrire (children texte uniquement) */
	typing?: boolean;
	children?: React.ReactNode;
}

/** Animations désactivées : préférence système OU harnais E2E (le flag
 * dtf_v2_no_autoinspect est déjà semé par toutes les suites de test —
 * sans ça, elles liraient des bulles à moitié tapées). */
const noAnim = () =>
	(typeof window !== 'undefined' &&
		!!window.matchMedia &&
		window.matchMedia('(prefers-reduced-motion: reduce)').matches) ||
	localStorage.getItem('dtf_v2_no_autoinspect') === '1';

const useTypewriter = (text: string, enabled: boolean) => {
	const skip = !enabled || !text || noAnim();
	const [n, setN] = useState(skip ? text.length : 0);
	useEffect(() => {
		if (skip) {
			setN(text.length);
			return undefined;
		}
		setN(0);
		let i = 0;
		const iv = window.setInterval(() => {
			i += 2;
			if (i >= text.length) {
				i = text.length;
				window.clearInterval(iv);
			}
			setN(i);
		}, 22);
		return () => window.clearInterval(iv);
	}, [text, skip]);
	return { shown: text.slice(0, n), done: n >= text.length };
};

/** Mascotte marmotte + bulle d'explication colorée (vert = bon, ambre = attention, rouge = problème). */
const Marmotte: React.FC<IProps> = ({ pose, tone = 'info', title, size = 64, className, hero, typing, children }) => {
	const plain = typeof children === 'string' ? children : '';
	const tw = useTypewriter(plain, !!typing && !!plain);
	const body =
		typing && plain ? (
			<>
				{tw.shown}
				{!tw.done && <span className="dtf-mmt-caret" aria-hidden="true" />}
			</>
		) : (
			children
		);
	const img = (
		<img
			src={MARMOTTE_POSES[pose]}
			alt=""
			className="dtf-marmotte-img"
			style={{ width: size, height: size }}
			draggable={false}
		/>
	);
	return (
		<div className={`dtf-marmotte dtf-marmotte-${tone}${hero ? ' dtf-marmotte-hero' : ''}${className ? ` ${className}` : ''}`}>
			{hero ? (
				<div className="dtf-mmt-stage">
					<span className="dtf-mmt-halo" aria-hidden="true" />
					<span className="dtf-mmt-spark dtf-mmt-spark-1" aria-hidden="true">
						✦
					</span>
					<span className="dtf-mmt-spark dtf-mmt-spark-2" aria-hidden="true">
						✧
					</span>
					<span className="dtf-mmt-spark dtf-mmt-spark-3" aria-hidden="true">
						✦
					</span>
					{img}
				</div>
			) : (
				img
			)}
			<div className="dtf-marmotte-bubble">
				{title && <div className="dtf-marmotte-title">{title}</div>}
				{children && <div className="dtf-marmotte-text">{body}</div>}
			</div>
		</div>
	);
};

export interface MarmotteVerdict {
	pose: MarmottePose;
	tone: MarmotteTone;
	title: string;
	text: string;
}

/** Verdict qualité « qui saute aux yeux » pour un logo à sa taille actuelle.
 * Priorité : analyse en cours → fichier impossible → traits fins → DPI trop bas
 * → vectoriel parfait → raster propre. */
export function marmotteVerdict(
	item: DTFItem,
	q: QualityInfo | undefined,
	minDpi: number,
	thin: boolean,
): MarmotteVerdict {
	const t = (k: string, o?: Record<string, unknown>) => i18next.t(`dtf.${k}`, o) as string;
	if (!q) return { pose: 'thinking', tone: 'info', title: t('mmt-analyse-t'), text: t('mmt-analyse') };
	const range = rangeText(q, item.kind);
	const dpiNow = item.kind === 'raster' ? Math.round(q.pxW / (item.widthCm / 2.54)) : null;
	const dpiBad = dpiNow !== null && dpiNow < minDpi;
	if (range.bad) return { pose: 'thinking', tone: 'bad', title: t('mmt-bad-t'), text: range.txt };
	if (thin) return { pose: 'thinking', tone: 'warn', title: t('mmt-thin-t'), text: t('mmt-thin') };
	if (dpiBad)
		return {
			pose: 'magic',
			tone: 'warn',
			title: t('mmt-dpi-t', { w: item.widthCm }),
			text: t('mmt-dpi', { dpi: dpiNow, min: minDpi }),
		};
	if (item.kind !== 'raster')
		return { pose: 'vector', tone: 'ok', title: t('mmt-vecto-t'), text: t('mmt-vecto') };
	return {
		pose: 'thumbs',
		tone: 'ok',
		title: t('mmt-ok-t'),
		text: dpiNow !== null ? t('mmt-ok-raster', { w: item.widthCm, dpi: dpiNow }) : t('mmt-ok'),
	};
}

/** Bulle verdict prête à l'emploi (wizard, fiche qualité). */
export const MarmotteQuality: React.FC<{
	item: DTFItem;
	q: QualityInfo | undefined;
	minDpi: number;
	thin: boolean;
	size?: number;
	className?: string;
}> = ({ item, q, minDpi, thin, size, className }) => {
	const v = marmotteVerdict(item, q, minDpi, thin);
	// key = tone : rejoue l'animation pop quand le verdict change (largeur modifiée…)
	return (
		<Marmotte key={v.tone} pose={v.pose} tone={v.tone} title={v.title} size={size} className={className}>
			{v.text}
		</Marmotte>
	);
};

export default Marmotte;
